// QuickBookingWidget.jsx - Complete Clean Version with ShopCode as Primary Key
import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { 
    Dialog, 
    Button, 
    Dropdown, 
    Calendar, 
    InputNumber, 
    InputTextarea, 
    Toast,
    Message 
} from 'primereact';
import useAuth from 'hooks/useAuth';
import useApi from "hooks/useApi";
import useApp from "hooks/useApp";


const API_URL = process.env.REACT_APP_API_PATH;
const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_SGuxeIUsmygKAW';

const QuickBookingWidget = ({ user, onSuccess }) => {
    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [services, setServices] = useState([]);
    const [allShops, setAllShops] = useState([]);
    const [nearbyShops, setNearbyShops] = useState([]);
    const [servicesLoading, setServicesLoading] = useState(false);
    const [shopsLoading, setShopsLoading] = useState(false);
    const [userLocation, setUserLocation] = useState(null);
    const [locationError, setLocationError] = useState(null);
    const [gettingAddress, setGettingAddress] = useState(false);
   
    const [formData, setFormData] = useState({
        serviceId: null,
        id :0,
        shopId: null,  // This will store ShopCode (string)
        pickupDate: null,
        deliveryDate: null,
        items: 5,
        instructions: '',
        pickupAddress: '',
        deliveryAddress: ''
    });
    const [errors, setErrors] = useState({});
    const toast = useRef(null);
    const auth = useAuth();
    const app = useApp();
    const api = useApi();
    const [responseMsg, setResponseMsg] = useState("");

  // Get minimum pickup date (current time + 3 hours)
const getMinPickupDate = () => {
    const minDate = new Date();
    minDate.setHours(minDate.getHours() + 3);
    minDate.setMinutes(0);
    minDate.setSeconds(0);
    minDate.setMilliseconds(0);
    return minDate;
};

// Get default pickup date (current time + 3 hours, rounded to next hour)
const getDefaultPickupDate = () => {
    const date = new Date();
    date.setHours(date.getHours() + 3);
    date.setMinutes(0);
    date.setSeconds(0);
    date.setMilliseconds(0);
    return date;
};

// Get minimum delivery date (pickup date + 24 hours)
const getMinDeliveryDate = (pickupDate) => {
    if (!pickupDate) return null;
    const minDate = new Date(pickupDate);
    minDate.setHours(minDate.getHours() + 24);
    return minDate;
};

// Get default delivery date (pickup date + 24 hours)
const getDefaultDeliveryDate = (pickupDate) => {
    if (!pickupDate) return null;
    const date = new Date(pickupDate);
    date.setHours(date.getHours() + 24);
    return date;
};


    // Reverse geocoding to get address from coordinates
    const getAddressFromCoords = async (latitude, longitude) => {
        setGettingAddress(true);
        try {
            // Using OpenStreetMap Nominatim API (free, no API key required)
            const response = await axios.get(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`
            );
            
            if (response.data && response.data.display_name) {
                const address = response.data.display_name;
                setFormData(prev => ({
                    ...prev,
                    pickupAddress: address
                }));
                
                toast.current?.show({
                    severity: 'success',
                    summary: 'Location Found',
                    detail: 'Pickup address automatically set from your location',
                    life: 3000
                });
                return address;
            }
        } catch (error) {
            console.error('Error getting address:', error);
            // Fallback: Use coordinates as address
            const fallbackAddress = `${latitude}, ${longitude}`;
            setFormData(prev => ({
                ...prev,
                pickupAddress: fallbackAddress
            }));
            
            toast.current?.show({
                severity: 'warn',
                summary: 'Partial Location',
                detail: 'Could not get full address, using coordinates',
                life: 3000
            });
            return fallbackAddress;
        } finally {
            setGettingAddress(false);
        }
    };

    const getCurrentLocation = () => {
        if (!navigator.geolocation) {
            toast.current?.show({
                severity: 'error',
                summary: 'Not Supported',
                detail: 'Geolocation is not supported by your browser',
                life: 5000
            });
            return;
        }

        navigator.geolocation.getCurrentPosition(
            async (position) => {
                const { latitude, longitude } = position.coords;
                
                console.log("Lat:", latitude);
                console.log("Lng:", longitude);
                
                // Store coordinates for distance calculation
                setUserLocation({
                    lat: latitude,
                    lng: longitude
                });
                
                // Get address from coordinates
                await getAddressFromCoords(latitude, longitude);
            },
            (error) => {
                console.error(error);
                let errorMessage = "Unable to retrieve location";
                
                switch(error.code) {
                    case error.PERMISSION_DENIED:
                        errorMessage = "Location access denied. Please enable location services.";
                        break;
                    case error.POSITION_UNAVAILABLE:
                        errorMessage = "Location information is unavailable.";
                        break;
                    case error.TIMEOUT:
                        errorMessage = "Location request timed out.";
                        break;
                    default:
                        errorMessage = "Unable to retrieve your location.";
                }
                
                toast.current?.show({
                    severity: 'error',
                    summary: 'Location Error',
                    detail: errorMessage,
                    life: 5000
                });
                setLocationError(errorMessage);
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
            }
        );
    };

    // Calculate distance between two coordinates using Haversine formula
    const calculateDistance = (lat1, lon1, lat2, lon2) => {
        if (!lat1 || !lon1 || !lat2 || !lon2) return Infinity;
        
        const R = 6371;
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a = 
            Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
            Math.sin(dLon/2) * Math.sin(dLon/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        return R * c;
    };

    // Filter shops within radius and sort by distance
    const filterNearbyShops = (userLat, userLng, shops) => {
        const shopsWithDistance = shops
            .filter(shop => {
                if (shop.status !== 'Active') return false;
                if (!shop.latitude || !shop.longitude) return false;
                
                const distance = calculateDistance(
                    userLat, 
                    userLng, 
                    parseFloat(shop.latitude), 
                    parseFloat(shop.longitude)
                );
                
                const maxRadius = shop.serviceRadius || 25;
                return distance <= maxRadius;
            })
            .map(shop => ({
                ...shop,
                distance: calculateDistance(
                    userLat, 
                    userLng, 
                    parseFloat(shop.latitude), 
                    parseFloat(shop.longitude)
                )
            }))
            .sort((a, b) => a.distance - b.distance);
        
        return shopsWithDistance;
    };

    // Get user's current location for shop filtering
    const getUserLocationForShops = () => {
        return new Promise((resolve, reject) => {
            if (!navigator.geolocation) {
                reject(new Error('Geolocation is not supported by your browser'));
                return;
            }
            
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const location = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude
                    };
                    setUserLocation(location);
                    setLocationError(null);
                    resolve(location);
                },
                (error) => {
                    let errorMessage = 'Unable to get your location. ';
                    switch(error.code) {
                        case error.PERMISSION_DENIED:
                            errorMessage += 'Please enable location access to find nearby shops.';
                            break;
                        case error.POSITION_UNAVAILABLE:
                            errorMessage += 'Location information is unavailable.';
                            break;
                        case error.TIMEOUT:
                            errorMessage += 'Location request timed out.';
                            break;
                        default:
                            errorMessage += 'Please enter your address manually.';
                    }
                    setLocationError(errorMessage);
                    reject(new Error(errorMessage));
                },
                {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 0
                }
            );
        });
    };

    // Fetch all shops from database
    const fetchAllShops = async () => {
        setShopsLoading(true);
        try {
            const token =  localStorage.getItem('token');
            const response = await axios.get(`${API_URL}customer/shops`, {
                headers: { 'Authorization': `Bearer ${auth.accessToken}` }
            });
            
            const shopsData = Array.isArray(response.data) ? response.data : [];
            setAllShops(shopsData);
            return shopsData;
        } catch (error) {
            console.error('Error fetching shops:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load shops',
                life: 3000
            });
            return [];
        } finally {
            setShopsLoading(false);
        }
    };

    // Fetch services from database
   useEffect(() => {
    const fetchServices = async () => {
        setServicesLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}customer/service`, {
                headers: { 'Authorization': `Bearer ${auth.accessToken}` }
            });
            const servicesData = Array.isArray(response.data) ? response.data : [];
            setServices(servicesData);
            
            if (servicesData && servicesData.length > 0) {
                const defaultPickup = getDefaultPickupDate();
                const defaultDelivery = getDefaultDeliveryDate(defaultPickup);
                setFormData(prev => ({
                    ...prev,
                    serviceId: servicesData[0].id,
                    pickupDate: defaultPickup,
                    deliveryDate: defaultDelivery
                }));
            }
        } catch (error) {
            console.error('Error fetching services:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load services',
                life: 3000
            });
        } finally {
            setServicesLoading(false);
        }
    };
    
    fetchServices();
    fetchAllShops();
}, []);


    // Filter nearby shops when user location or all shops change
    useEffect(() => {
        if (userLocation && allShops.length > 0) {
            const nearby = filterNearbyShops(userLocation.lat, userLocation.lng, allShops);
            setNearbyShops(nearby);
            
            if (nearby.length > 0) {
                setFormData(prev => ({
                    ...prev,
                    shopId: nearby[0].shopCode  // Store ShopCode (string)
                }));
            }
        }
    }, [userLocation, allShops]);

    // Get selected shop object by ShopCode
    const getSelectedShop = () => {
        if (!formData.shopId) return null;
        return nearbyShops.find(shop => shop.shopCode === formData.shopId);
    };

    const selectedShop = getSelectedShop();

    // Handle dialog open
    const handleDialogOpen = async () => {
        setVisible(true);
        setLocationError(null);
        
        // Set default pickup address from user profile if available
        if (user?.address && !formData.pickupAddress) {
            setFormData(prev => ({
                ...prev,
                pickupAddress: user.address
            }));
        }
        
        setFormData(prev => ({
            ...prev,
            shopId: null
        }));
        
        // Get current location for address and shops
        if (allShops.length > 0) {
            try {
                // Get location for shops filtering
                await getUserLocationForShops();
                // Get address for pickup
                getCurrentLocation();
            } catch (error) {
                console.error('Location error:', error);
            }
        }
    };

    // Handle shop selection - stores ShopCode
    const handleShopChange = (e) => {
        console.log("Selected ShopCode:", e.value); // Should be string like "SHP001"
        
        setFormData(prev => ({
            ...prev,
            shopId: e.value  // Store ShopCode string
        }));
        
        setErrors(prev => ({
            ...prev,
            shopId: ''
        }));
    };

   const handleInputChange = (field, value) => {
    if (field === 'pickupDate') {
        const minPickup = getMinPickupDate();
        
        // Check if selected date is valid
        if (value < minPickup) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Invalid Pickup Time',
                detail: `Pickup must be at least 3 hours from now. Minimum: ${minPickup.toLocaleString()}`,
                life: 3000
            });
            return;
        }
        
        // Auto-update delivery date when pickup changes
        const defaultDelivery = getDefaultDeliveryDate(value);
        setFormData(prev => ({
            ...prev,
            pickupDate: value,
            deliveryDate: defaultDelivery
        }));
        
        // Clear delivery date error if exists
        if (errors.deliveryDate) {
            setErrors(prev => ({
                ...prev,
                deliveryDate: ''
            }));
        }
        return;
    }
    
    if (field === 'deliveryDate') {
        if (formData.pickupDate) {
            const minDelivery = getMinDeliveryDate(formData.pickupDate);
            if (value < minDelivery) {
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Invalid Delivery Time',
                    detail: `Delivery must be at least 24 hours after pickup. Minimum: ${minDelivery.toLocaleString()}`,
                    life: 3000
                });
                return;
            }
        }
    }
    
    setFormData(prev => ({
        ...prev,
        [field]: value
    }));
    
    if (errors[field]) {
        setErrors(prev => ({
            ...prev,
            [field]: ''
        }));
    }
};

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.serviceId) {
        newErrors.serviceId = 'Service is required';
    }
    
    if (!formData.shopId) {
        newErrors.shopId = 'Please select a nearby shop';
    }
    
    if (!formData.pickupDate) {
        newErrors.pickupDate = 'Pickup date is required';
    } else {
        const minPickup = getMinPickupDate();
        if (formData.pickupDate < minPickup) {
            newErrors.pickupDate = `Pickup must be at least 3 hours from now (minimum: ${minPickup.toLocaleString()})`;
        }
    }
    
    if (!formData.deliveryDate) {
        newErrors.deliveryDate = 'Delivery date is required';
    } else if (formData.pickupDate) {
        const minDelivery = getMinDeliveryDate(formData.pickupDate);
        if (formData.deliveryDate < minDelivery) {
            newErrors.deliveryDate = `Delivery must be at least 24 hours after pickup (minimum: ${minDelivery.toLocaleString()})`;
        }
    }
    
    if (!formData.items || formData.items < 1 || formData.items > 50) {
        newErrors.items = 'Items must be between 1 and 50';
    }
    
    if (!formData.pickupAddress) {
        newErrors.pickupAddress = 'Pickup address is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
};


    const calculateTotal = () => {
        const selectedService = services.find(s => s.id === formData.serviceId);
        const basePrice = selectedService?.price || 0;
        const itemPrice = 2.5;
        return basePrice + (formData.items * itemPrice);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            toast.current?.show({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'Please fix the errors in the form',
                life: 3000
            });
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const totalAmount = calculateTotal();
            
            const bookingData = {
                serviceId: parseInt(formData.serviceId, 10),
                shopCode: formData.shopId,
                pickupDate: formData.pickupDate.toISOString(),
                deliveryDate: formData.deliveryDate.toISOString(),
                pickupAddress: formData.pickupAddress || user?.address || '',
                deliveryAddress: formData.deliveryAddress || user?.address || '',
                totalAmount: parseFloat(totalAmount),
                totalItems: parseInt(formData.items, 10),
                specialInstructions: formData.instructions || ''
            };

            console.log("Sending booking data:", bookingData);

            const response = await axios.post(`${API_URL}customer/qbooking`, bookingData, {
                headers: { 
                    'Authorization': `Bearer ${auth.accessToken}`,
                    'Content-Type': 'application/json'
                }
            });
            
            toast.current?.show({
                severity: 'success',
                summary: 'Booking Confirmed!',
                detail: `Booking #${response.data.bookingReference} created successfully. Total: ₹${totalAmount.toFixed(2)}`,
                life: 5000
            });

            // Call sendWhatsApp with the response data
            await sendWhatsApp(response.data);

            // Reset form
            const minPickup = getMinPickupDate();
            const minDelivery = getMinDeliveryDate(minPickup);
            setFormData({
                serviceId: services[0]?.id || null,
                shopId: null,
                pickupDate: minPickup,
                deliveryDate: minDelivery,
                items: 5,
                instructions: '',
                pickupAddress: '',
                deliveryAddress: ''
            });
            setErrors({});
            setVisible(false);
            
            if (onSuccess) onSuccess();
        } catch (error) {
            console.error('Booking error:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Booking Failed',
                detail: error.response?.data?.message || 'Failed to create booking. Please try again.',
                life: 5000
            });
        } finally {
            setLoading(false);
        }
    };

    // Define sendWhatsApp outside handleSubmit, but inside the component
    const sendWhatsApp = async (bookingResponse) => {
        try {
            const res = await axios.post(
                `${API_URL}whats/send-invoice`,
                {
                    BookingId: bookingResponse.Id,
                    CustomerPhone: bookingResponse.CustomerPhone,
                    CustomerName: bookingResponse.CustomerName || "Customer",
                    Amount: bookingResponse.TotalAmount,
                    PaymentId: "N/A"
                },
                {
                    headers: {
                        Authorization: `Bearer ${auth.accessToken}`,
                        "Content-Type": "application/json"
                    }
                }
            );

            if (res.data.success) {
                console.log("✅ WhatsApp message sent:", res.data.message);
                toast.current?.show({
                    severity: 'info',
                    summary: 'WhatsApp Notification',
                    detail: 'Invoice sent via WhatsApp',
                    life: 3000
                });
            } else {
                console.error("❌ WhatsApp error:", res.data.message);
                // Don't show error toast for WhatsApp failure to not confuse user
            }
        } catch (error) {
            console.error("WhatsApp send error:", error);
            // Silent fail for WhatsApp - booking is already confirmed
        }
    };

    // Add a button to manually refresh location
    const refreshLocation = () => {
        getCurrentLocation();
        getUserLocationForShops();
    };

    const selectedService = services.find(s => s.id === formData.serviceId);
    const minPickupDate = getMinPickupDate();
    const minDeliveryDate = formData.pickupDate ? getMinDeliveryDate(formData.pickupDate) : null;

    // Shop dropdown templates
    const shopOptionTemplate = (shop) => {
        if (!shop) return null;
        return (
            <div className="flex flex-column p-2">
                <div className="font-semibold">{shop.shopName}</div>
                <div className="text-sm text-gray-600">
                    {shop.distance ? `${shop.distance.toFixed(1)} km away` : 'Distance unavailable'}
                    {shop.rating ? ` • ⭐ ${shop.rating.toFixed(1)}` : ''}
                    {shop.address ? ` • ${shop.address.split(',')[0]}` : ''}
                </div>
                <div className="text-xs text-gray-400 mt-1">
                    Shop Code: {shop.shopCode}
                </div>
            </div>
        );
    };

    const selectedShopTemplate = (option) => {
        if (!option) return <span>Select a shop</span>;
        
        return (
            <div className="flex align-items-center">
                <span className="font-medium">{option.shopName}</span>
                {option.distance && (
                    <span className="text-sm text-gray-500 ml-2">
                        ({option.distance.toFixed(1)} km)
                    </span>
                )}
                <span className="text-xs text-gray-400 ml-2">
                    [{option.shopCode}]
                </span>
            </div>
        );
    };

    return (
        <>
            <Toast ref={toast} position="top-right" />
            
            <Button 
                label="Quick Book" 
                icon="pi pi-bolt" 
                className="p-button-success p-button-rounded"
                onClick={handleDialogOpen}
                tooltip="Quick laundry booking"
                tooltipOptions={{ position: 'bottom' }}
            />

            <Dialog 
                header="Quick Laundry Booking" 
                visible={visible} 
                style={{ width: '600px', maxWidth: '90vw' }}
                onHide={() => {
                    setVisible(false);
                    setErrors({});
                    setLocationError(null);
                }}
                draggable={false}
                resizable={false}
            >
                <form onSubmit={handleSubmit} className="p-fluid">
                    {/* Shop Selection */}
                    <div className="field mb-3">
                        <label htmlFor="shop" className="block font-medium mb-2">
                            Select Shop <span className="text-red-500">*</span>
                        </label>
                        
                        {locationError && (
                            <Message 
                                severity="warn" 
                                text={locationError}
                                className="mb-2"
                                closable
                            />
                        )}
                        
                        <Dropdown
                            id="shop"
                            value={formData.shopId}
                            options={nearbyShops}
                            optionLabel="shopName"
                            optionValue="shopCode"  // Use ShopCode as value
                            onChange={handleShopChange}
                            itemTemplate={shopOptionTemplate}
                            valueTemplate={selectedShopTemplate}
                            placeholder="Select a shop"
                            loading={shopsLoading}
                        />
                        
                        {errors.shopId && (
                            <small className="p-error block mt-1">{errors.shopId}</small>
                        )}
                        
                        {/* Shop Details Card */}
                        {selectedShop && (
                            <div className="mt-3 p-3 bg-blue-50 border-round shadow-sm">
                                <div className="flex justify-content-between align-items-start mb-2">
                                    <div>
                                        <i className="pi pi-store mr-2 text-primary"></i>
                                        <strong className="text-lg">{selectedShop.shopName}</strong>
                                        <div className="text-xs text-gray-500 mt-1">
                                            Shop Code: {selectedShop.shopCode}
                                        </div>
                                    </div>
                                    {selectedShop.rating && (
                                        <span className="bg-yellow-100 px-2 py-1 border-round text-sm font-semibold">
                                            ⭐ {selectedShop.rating.toFixed(1)}
                                        </span>
                                    )}
                                </div>
                                
                                <div className="text-sm text-gray-700 mt-2">
                                    <div className="mb-1">
                                        <i className="pi pi-map-marker mr-1 text-primary"></i>
                                        <strong>Address:</strong> {selectedShop.address}, {selectedShop.city}
                                    </div>
                                    
                                    <div className="grid mt-2">
                                        <div className="col-6">
                                            <i className="pi pi-compass mr-1 text-primary"></i>
                                            <strong>Distance:</strong> {selectedShop.distance?.toFixed(1)} km
                                        </div>
                                        <div className="col-6">
                                            <i className="pi pi-phone mr-1 text-primary"></i>
                                            <strong>Contact:</strong> {selectedShop.contactPhone}
                                        </div>
                                    </div>
                                    
                                    {selectedShop.currentLoad >= selectedShop.capacityPerDay && (
                                        <div className="mt-2 text-warning text-sm bg-yellow-50 p-2 border-round">
                                            <i className="pi pi-exclamation-triangle mr-1"></i>
                                            <strong>Note:</strong> Shop is at full capacity today
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        
                        {/* No Shops Message */}
                        {!locationError && nearbyShops.length === 0 && !shopsLoading && allShops.length > 0 && (
                            <div className="mt-3 p-3 bg-yellow-50 border-round">
                                <i className="pi pi-info-circle mr-1 text-warning"></i>
                                <span className="text-sm">No shops found within 25km of your location.</span>
                                <Button 
                                    label="Refresh Location" 
                                    icon="pi pi-refresh" 
                                    className="p-button-text p-button-sm mt-2"
                                    onClick={refreshLocation}
                                    type="button"
                                />
                            </div>
                        )}
                        
                        {/* Loading Shops */}
                        {shopsLoading && (
                            <div className="mt-3 p-3 text-center">
                                <i className="pi pi-spin pi-spinner mr-2"></i>
                                <span>Finding shops near you...</span>
                            </div>
                        )}
                    </div>

                    {/* Service Selection */}
                    <div className="field mb-3">
                        <label htmlFor="service" className="block font-medium mb-2">
                            Service Type <span className="text-red-500">*</span>
                        </label>
                        <Dropdown
                            id="service"
                            value={formData.serviceId}
                            options={services}
                            optionLabel="name"
                            optionValue="id"
                            placeholder={servicesLoading ? "Loading services..." : "Select Service"}
                            onChange={(e) => handleInputChange('serviceId', e.value)}
                            className={errors.serviceId ? 'p-invalid' : ''}
                            disabled={servicesLoading}
                        />
                        {errors.serviceId && (
                            <small className="p-error block mt-1">{errors.serviceId}</small>
                        )}
                    </div>

                    {/* Date Selection */}
                    <div className="grid mb-3">
                        <div className="col-6">
                            <label htmlFor="pickupDate" className="block font-medium mb-2">
                                Pickup Date <span className="text-red-500">*</span>
                            </label>
                            <Calendar
                                id="pickupDate"
                                value={formData.pickupDate}
                                onChange={(e) => handleInputChange('pickupDate', e.value)}
                                showIcon
                                dateFormat="dd/mm/yy"
                                showTime
                                hourFormat="12"
                                minDate={minPickupDate}
                                className={errors.pickupDate ? 'p-invalid' : ''}
                                disabled={servicesLoading}
                            />
                            {errors.pickupDate && (
                                <small className="p-error block mt-1">{errors.pickupDate}</small>
                            )}
                            <small className="text-gray-500 block mt-1">
                                Minimum 3 hours from now
                            </small>
                        </div>
                        <div className="col-6">
                            <label htmlFor="deliveryDate" className="block font-medium mb-2">
                                Delivery Date <span className="text-red-500">*</span>
                            </label>
                            <Calendar
                                id="deliveryDate"
                                value={formData.deliveryDate}
                                onChange={(e) => handleInputChange('deliveryDate', e.value)}
                                showIcon
                                dateFormat="dd/mm/yy"
                                showTime
                                hourFormat="12"
                                minDate={minDeliveryDate}
                                className={errors.deliveryDate ? 'p-invalid' : ''}
                                disabled={servicesLoading || !formData.pickupDate}
                            />
                            {errors.deliveryDate && (
                                <small className="p-error block mt-1">{errors.deliveryDate}</small>
                            )}
                            <small className="text-gray-500 block mt-1">
                                Minimum 24 hours after pickup
                            </small>
                        </div>
                    </div>

                    {/* Number of Items */}
                    <div className="field mb-3">
                        <label htmlFor="items" className="block font-medium mb-2">
                            Number of Items <span className="text-red-500">*</span>
                        </label>
                        <InputNumber
                            id="items"
                            value={formData.items}
                            onValueChange={(e) => handleInputChange('items', e.value)}
                            min={1}
                            max={50}
                            showButtons
                            buttonLayout="horizontal"
                            incrementButtonIcon="pi pi-plus"
                            decrementButtonIcon="pi pi-minus"
                            className={errors.items ? 'p-invalid' : ''}
                            disabled={servicesLoading}
                        />
                        {errors.items && (
                            <small className="p-error block mt-1">{errors.items}</small>
                        )}
                    </div>

                    {/* Pickup Address */}
                    <div className="field mb-3">
                        <label htmlFor="pickupAddress" className="block font-medium mb-2">
                            Pickup Address <span className="text-red-500">*</span>
                        </label>
                        <div className="flex gap-2">
                            <InputTextarea
                                id="pickupAddress"
                                value={formData.pickupAddress}
                                onChange={(e) => handleInputChange('pickupAddress', e.target.value)}
                                rows={2}
                                placeholder={gettingAddress ? "Getting your location..." : (user?.address || "Enter pickup address")}
                                className="w-full"
                                disabled={servicesLoading || gettingAddress}
                            />
                            <Button 
                                icon="pi pi-map-marker" 
                                className="p-button-outlined"
                                onClick={refreshLocation}
                                type="button"
                                tooltip="Get Location"
                                loading={gettingAddress}
                                disabled={gettingAddress}
                            />
                        </div>
                        {errors.pickupAddress && (
                            <small className="p-error block mt-1">{errors.pickupAddress}</small>
                        )}
                        {!formData.pickupAddress && !gettingAddress && (
                            <small className="text-gray-500 block mt-1">
                                Click the location button to auto-fill your current address
                            </small>
                        )}
                        {gettingAddress && (
                            <small className="text-primary block mt-1">
                                <i className="pi pi-spin pi-spinner mr-1"></i>
                                Fetching your location...
                            </small>
                        )}
                    </div>

                    {/* Special Instructions */}
                    <div className="field mb-4">
                        <label htmlFor="instructions" className="block font-medium mb-2">
                            Special Instructions
                        </label>
                        <InputTextarea
                            id="instructions"
                            value={formData.instructions}
                            onChange={(e) => handleInputChange('instructions', e.target.value)}
                            rows={2}
                            placeholder="Any special requirements, stains, or preferences..."
                            className="w-full"
                            disabled={servicesLoading}
                        />
                    </div>

                    {/* Price Summary */}
                    {selectedService && (
                        <div className="mb-4 p-3 surface-50 border-round">
                            <h5 className="mt-0 mb-2">Booking Summary</h5>
                            <div className="grid">
                                <div className="col-6">
                                    <div className="text-sm text-gray-600">Service</div>
                                    <div className="font-medium">{selectedService.name}</div>
                                </div>
                                <div className="col-6 text-right">
                                    <div className="text-sm text-gray-600">Base Price</div>
                                    <div>₹{selectedService.price?.toFixed(2)}</div>
                                </div>
                            </div>
                            <div className="grid mt-2">
                                <div className="col-6">
                                    <div className="text-sm text-gray-600">Items ({formData.items})</div>
                                    <div>@ $2.50 each</div>
                                </div>
                                <div className="col-6 text-right">
                                    <div className="text-sm text-gray-600">Items Total</div>
                                    <div>₹{(formData.items * 2.5).toFixed(2)}</div>
                                </div>
                            </div>
                            <div className="flex justify-content-between align-items-center mt-3 pt-3 border-top-1 surface-border">
                                <div className="font-bold">Total Amount</div>
                                <div className="text-xl font-bold text-primary">₹{calculateTotal().toFixed(2)}</div>
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex justify-content-end gap-2 mt-4">
                        <Button 
                            label="Cancel" 
                            className="p-button-text" 
                            onClick={() => setVisible(false)}
                            disabled={loading || servicesLoading}
                            type="button"
                        />
                        <Button 
                            label={loading ? 'Processing...' : 'Confirm Booking'} 
                            icon={loading ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
                            type="submit"
                            disabled={loading || servicesLoading || shopsLoading || nearbyShops.length === 0}
                            loading={loading}
                            className="p-button-success"
                        />
                    </div>
                </form>
            </Dialog>
        </>
    );
};

export default QuickBookingWidget;